import { getTimePartsFromSeconds } from '@superbet-group/web.lib.utilities';
import { padStart as _padStart } from 'lodash';

const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;

export enum TimeInterval {
    h = 'h',
    m = 'm',
    s = 's',
}

interface AbsoluteTimeOptions {
    inputTimeInterval: TimeInterval;
    outputTimeFormat?: [TimeInterval.h] |
        [TimeInterval.h, TimeInterval.m] |
        [TimeInterval.h, TimeInterval.m, TimeInterval.s] |
        [TimeInterval.m] |
        [TimeInterval.m, TimeInterval.s] |
        [TimeInterval.s];
}

/**
 * Displays a time period of units in options.inputTimeInterval in a specific format
 * specified in options.outputTimeFormat
 * @param value {number} the amount of time units to display
 * in minutes
 * @param options {Object} see types
 * Currently only displays an amount of minutes in hh:mm format
 */
export function absoluteTime(
    value: number,
    options: AbsoluteTimeOptions = {
        inputTimeInterval: TimeInterval.m,
        outputTimeFormat: [TimeInterval.h, TimeInterval.m],
    }) {
    const amount = Math.floor(convertToSeconds(value, options.inputTimeInterval));

    const [hours, minutes, seconds] = getTimePartsFromSeconds(amount);

    return formatTime(
        {
            [TimeInterval.h]: hours,
            [TimeInterval.m]: minutes,
            [TimeInterval.s]: seconds,
        },
        options.outputTimeFormat,
    );
}

export function countedTime(time: number) {
    const [hours, minutes, seconds] = getTimePartsFromSeconds(time);

    return formatHMS(hours, minutes, seconds);
}

const convertToSeconds = (value: number, type: TimeInterval) => {
    const converter: Record<TimeInterval, number> = {
        [TimeInterval.h]: SECONDS_IN_HOUR,
        [TimeInterval.m]: SECONDS_IN_MINUTE,
        [TimeInterval.s]: 1,
    };
    return converter[type] * value;
};

function padWithZeroes(time: number) {
    return `${time}`.padStart(2, '0');
}

function formatTime(timestamp: Record<TimeInterval, number>, outputTimeFormat = [TimeInterval.h, TimeInterval.m]) {
    return outputTimeFormat.map((unit) => _padStart(timestamp[unit].toString(), 2, '0')).join(':');
}

function formatHMS(hours: number, minutes: number, seconds: number) {
    return `${padWithZeroes(hours)}:${padWithZeroes(minutes)}:${padWithZeroes(seconds)}`;
}
